import { useEffect } from 'react';
import Layout from '@/components/Layout'
import { TrackingScripts } from '@/components/TrackingScripts'
import { toastOptions } from '@/config/toast.config'
import { useFacebookPixel } from '@/hooks/useFacebookPixel'
import { useReferral } from '@/hooks/useReferral'
import '@/styles/globals.css'
import '@/styles/toast.css'
import { CartProvider } from '@/contexts/CartContext'
import { LoginModalProvider, useLoginModal } from '@/contexts/LoginModalContext'
import { PersonalizeModalProvider, usePersonalizeModal } from '@/contexts/PersonalizeModalContext'
import { AIPersonalizeModalProvider, useAIPersonalizeModal } from '@/contexts/AIPersonalizeModalContext';
import { NotebookModalProvider, useNotebookModal } from '@/contexts/NotebookModalContext';
import type { AppProps } from 'next/app'
import { GoogleAnalytics } from 'nextjs-google-analytics'
import React from 'react'
import { Toaster } from 'react-hot-toast'
import ToastLimiter from '@/utils/toastManager'
import { QueryClient, QueryClientProvider } from 'react-query'
import LoginModal from '@/components/Auth/LoginModal'
import PersonalizeModal from '@/components/Personalize/PersonalizeModal'
import AIPersonalizeModal from '@/components/Personalize/AIPersonalizeModal';
import NotebookModal from '@/components/Personalize/NotebookModal';
import { CTAProvider } from '@/contexts/CTAContext'
import { appWithTranslation } from 'next-i18next'
import '../../il8n'
import { useLocaleDetection } from '@/hooks/useLocaleDetection';
import LocaleConfirmationModal from '@/components/LocaleConfirmationModal';
import { Poppins } from '@next/font/google' // Add this import
import { useRouter } from 'next/router' // Add this import
import { track } from '@/lib/tracker';

const queryClient = new QueryClient()

// Configure Poppins font
const poppins = Poppins({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700'],
  variable: '--font-poppins',
  display: 'swap',
});

function GlobalModals() {
  const {
    isOpen,
    setIsOpen,
    postLoginCallback,
    setPostLoginCallback,
  } = useLoginModal();

  const { isOpen: isPersonalizeOpen, closeModal: closePersonalizeModal, productData: personalizeModalProductData } = usePersonalizeModal();
  const { isOpen: isAIPersonalizeOpen, closeModal: AIModalClose, productData: AIModalProductData } = useAIPersonalizeModal();
  const { isOpen: isNotebookOpen, closeModal: closeNotebookModal, productData: notebookProductData } = useNotebookModal();

  // Get locale detection modal state
  const { showModal: showLocaleModal } = useLocaleDetection();

  // ✅ Combine modal open states (including locale modal)
  const anyModalOpen = isOpen || isPersonalizeOpen || isNotebookOpen || showLocaleModal;

  // ✅ Scroll lock logic
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const body = document.body;

    if (anyModalOpen) {
      const scrollEl = document.getElementById('page-scroll');
      if (scrollEl) {
        scrollEl.style.overflow = 'hidden';
      }
    } else {
      body.style.overflow = '';
    }

    return () => {
      body.style.overflow = '';
    };
  }, [anyModalOpen]);

  // ✅ After a successful login (modal closes), execute any stored callback
  useEffect(() => {
    if (!isOpen && postLoginCallback) {
      postLoginCallback();
      setPostLoginCallback(undefined);
    }
  }, [isOpen, postLoginCallback, setPostLoginCallback]);

  return (
    <>
      {isOpen && (
        <LoginModal/>
      )}

      {isPersonalizeOpen && personalizeModalProductData && (
        <PersonalizeModal
          setPersonalizeModal={closePersonalizeModal}
          product={personalizeModalProductData.product}
          inputs={personalizeModalProductData.inputs}
        />
      )}

      {isAIPersonalizeOpen && AIModalProductData && (
        <AIPersonalizeModal
          setPersonalizeModal={AIModalClose}
          product={AIModalProductData.product}
          inputs={AIModalProductData.inputs}
        />
      )}

      {isNotebookOpen && notebookProductData && (
        <NotebookModal
          setNotebookModal={closeNotebookModal}
          product={notebookProductData.product}
          inputs={notebookProductData.inputs}
        />
      )}
    </>
  );
}

function App({ Component, pageProps }: AppProps) {
  useFacebookPixel()
  useReferral()
  const router = useRouter()
  const { locale } = router

  // Use the locale detection hook
  const {
    detectedCountry,
    showModal,
    handleUserConfirmation
  } = useLocaleDetection()

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let sessionId = sessionStorage.getItem('session_started');

    // 🔹 SESSION START (once per tab)
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('session_started', sessionId);

      track({
        event_name: 'session_started',
        metadata: { sessionId },
      });
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;

      // Detect if this is a reload
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const isReload = navEntry?.type === 'reload';

      if (isReload) return; // ❌ Do NOT end session on refresh

      const storedSessionId = sessionStorage.getItem('session_started');
      if (!storedSessionId) return;

      track({
        event_name: 'session_ended',
        metadata: { sessionId: storedSessionId },
      });

      sessionStorage.removeItem('session_started');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <>
      {process.env.NODE_ENV === 'production' && <TrackingScripts />}
      <link
        rel="stylesheet"
        crossOrigin="anonymous"
        href={process.env.NEXT_PUBLIC_SITE_URL + '/fonts.css'}
      />
      <QueryClientProvider client={queryClient}>
        <CartProvider>
          <LoginModalProvider>
            <PersonalizeModalProvider>
              <AIPersonalizeModalProvider>
                <NotebookModalProvider>
                  <CTAProvider>
                    {/* Remove the wrapper div and apply font globally */}
                    <div className={locale === 'en' ? 'font-poppins' : ''}>
                      <Layout>
                        <>
                          <Toaster position="top-center" toastOptions={toastOptions} />
                          <ToastLimiter />
                          {process.env.NODE_ENV === 'production' && <GoogleAnalytics trackPageViews />}
                          <Component {...pageProps} />
                          <GlobalModals />
                          
                          {/* Locale Confirmation Modal */}
                          {detectedCountry && (
                            <LocaleConfirmationModal
                              isOpen={showModal}
                              detectedCountry={detectedCountry}
                              onConfirm={handleUserConfirmation}
                            />
                          )}
                        </>
                      </Layout>
                    </div>
                  </CTAProvider>
                </NotebookModalProvider>
              </AIPersonalizeModalProvider>
            </PersonalizeModalProvider>
          </LoginModalProvider>
        </CartProvider>
      </QueryClientProvider>
    </>
  )
}

export default appWithTranslation(App);